import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Lock, FileText, FlaskConical } from "lucide-react";
import { ROLE_OPTIONS, API } from "@/constants/roles";

const emptyForm = { name: "", discord: "", email: "", password: "", role: "Standard Animator", portfolio_url: "", message: "", test_path: "portfolio" };
const ANIMATOR_ROLES = ["Standard Animator", "High-Quality Animator"];

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return "Something went wrong. Please try again.";
};

export const ApplyForm = ({ selectedRole, roleStatus }) => {
  const isClosed = (r) => roleStatus[r] === false;
  const firstOpen = ROLE_OPTIONS.find((r) => !isClosed(r)) || ROLE_OPTIONS[0];
  const initialRole = isClosed(selectedRole) ? firstOpen : selectedRole;

  const [form, setForm] = useState({ ...emptyForm, role: initialRole });
  const [submitting, setSubmitting] = useState(false);
  const [packInfo, setPackInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/test-pack/public-info`).then((r) => setPackInfo(r.data)).catch(() => {});
  }, []);

  const role = form.role;
  const isAnimator = ANIMATOR_ROLES.includes(role);
  const tierInfo = role === "High-Quality Animator" ? packInfo?.hq : packInfo?.standard;
  const testOffered = isAnimator && tierInfo?.available;
  const testPath = testOffered && form.test_path === "test" ? "test" : "portfolio";

  const set = (key) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [key]: v, ...(key === "role" && !ANIMATOR_ROLES.includes(v) ? { test_path: "portfolio" } : {}) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/applications`, { ...form, role, test_path: testPath });
      localStorage.setItem("melto_token", data.token);
      toast.success(testPath === "test" ? "Application sent! Your animation test pack is waiting in your dashboard." : "Application sent! Your account is ready — track your status in the dashboard.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

  return (
    <section id="apply" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div className="text-center">
          <p className="font-mono-label text-xs font-bold uppercase tracking-widest text-cyan-600">Join the crew</p>
          <h2 data-testid="apply-heading" className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
            Send your application
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Takes two minutes. Your account is created as you apply — use it to track your status in the Dashboard anytime.
          </p>
        </div>

        <form
          data-testid="application-form"
          onSubmit={handleSubmit}
          className="mt-12 rounded-3xl border border-slate-200/80 bg-white p-7 shadow-sm sm:p-10"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="apply-name" className="mb-2 block text-xs font-semibold text-slate-700">
                Name / Artist Name
              </label>
              <input
                id="apply-name"
                data-testid="apply-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. MeltoFan123"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="apply-discord" className="mb-2 block text-xs font-semibold text-slate-700">
                Discord Username
              </label>
              <input
                id="apply-discord"
                data-testid="apply-discord-input"
                required
                minLength={2}
                value={form.discord}
                onChange={set("discord")}
                placeholder="e.g. blocky_animator"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="apply-email" className="mb-2 block text-xs font-semibold text-slate-700">
                Email
              </label>
              <input
                id="apply-email"
                data-testid="apply-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="apply-password" className="mb-2 block text-xs font-semibold text-slate-700">
                Password
              </label>
              <input
                id="apply-password"
                data-testid="apply-password-input"
                required
                type="password"
                minLength={8}
                value={form.password}
                onChange={set("password")}
                placeholder="Min. 8 characters"
                className={inputCls}
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            This email + password create your account — log in anytime via Dashboard to see your application status.
          </p>

          <div className="mt-5">
            <label htmlFor="apply-role" className="mb-2 block text-xs font-semibold text-slate-700">
              Applying For
            </label>
            <select
              id="apply-role"
              data-testid="apply-role-select"
              value={role}
              onChange={set("role")}
              className={inputCls}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r} disabled={isClosed(r)}>
                  {isClosed(r) ? `${r} — Closed` : r}
                </option>
              ))}
            </select>
            {ROLE_OPTIONS.some(isClosed) && (
              <p data-testid="apply-closed-note" className="mt-2 flex items-center gap-1.5 text-xs font-medium text-slate-400">
                <Lock className="h-3 w-3" /> Some roles are paused while the current hiring round fills up.
              </p>
            )}
          </div>

          {isAnimator && packInfo?.available && (
            <div className="mt-5" data-testid="apply-path-selector">
              <label className="mb-2 block text-xs font-semibold text-slate-700">How do you want to apply?</label>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  data-testid="apply-path-portfolio"
                  onClick={() => setForm((f) => ({ ...f, test_path: "portfolio" }))}
                  className={`rounded-2xl border-2 p-4 text-left transition-all ${testPath === "portfolio" ? "border-cyan-400 bg-cyan-50/60" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <FileText className="h-5 w-5 text-cyan-600" />
                  <p className="mt-2 text-sm font-bold text-slate-900">Apply with portfolio</p>
                  <p className="mt-0.5 text-xs text-slate-500">Share your existing work — we review it and get back to you.</p>
                </button>
                <button
                  type="button"
                  data-testid="apply-path-test"
                  onClick={() => setForm((f) => ({ ...f, test_path: "test" }))}
                  className={`rounded-2xl border-2 p-4 text-left transition-all ${testPath === "test" ? "border-violet-400 bg-violet-50/60" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <FlaskConical className="h-5 w-5 text-violet-500" />
                  <p className="mt-2 text-sm font-bold text-slate-900">Complete the {role === "High-Quality Animator" ? "HQ " : ""}animation test</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {tierInfo.title}{tierInfo.required_length ? ` · ${tierInfo.required_length}` : ""}{tierInfo.time_allowed ? ` · ${tierInfo.time_allowed}` : ""}
                  </p>
                </button>
              </div>
              {testPath === "test" && (
                <p data-testid="apply-test-pay-note" className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800">
                  <b>Paid test:</b> the animation test pays a flat <b>$20 USD</b> only if your completed test is approved as meeting
                  the required quality standard. Submitting alone does not create payment owed. You'll get the full test pack
                  (script, audio, assets, references) in your dashboard right after applying.
                </p>
              )}
            </div>
          )}

          <div className="mt-5">
            <label htmlFor="apply-portfolio" className="mb-2 block text-xs font-semibold text-slate-700">
              Portfolio / Showcase Link {testPath === "test" && <span className="font-normal text-slate-400">(optional when taking the test)</span>}
            </label>
            <input
              id="apply-portfolio"
              data-testid="apply-portfolio-input"
              required={testPath !== "test"}
              type="url"
              value={form.portfolio_url}
              onChange={set("portfolio_url")}
              placeholder="YouTube, Drive, Twitter/X, ArtStation..."
              className={inputCls}
            />
          </div>

          <div className="mt-5">
            <label htmlFor="apply-message" className="mb-2 block text-xs font-semibold text-slate-700">
              Your tools & favorite projects
            </label>
            <textarea
              id="apply-message"
              data-testid="apply-message-input"
              required
              minLength={10}
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us what you use (Moon Animator, Blender, Premiere...), what you've made, and why you want in."
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="apply-submit-btn"
            type="submit"
            disabled={submitting}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 py-4 text-base font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-cyan-600 hover:shadow-cyan-500/40 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {submitting ? "Sending..." : "Submit application"}
          </button>
          <p className="mt-4 text-center text-xs text-slate-400">
            Already applied? <a href="/login" data-testid="apply-login-link" className="font-semibold text-cyan-600 hover:text-cyan-700">Log in to your dashboard</a>
          </p>
        </form>
      </div>
    </section>
  );
};
